import {
  AlertTriangle, ArrowUpRight, Bell, Check, ClipboardList, Clock, Crosshair, FileText, Gavel,
  Globe, Landmark, Minus, MessageSquare, Plus, Rocket, Scale, Search,
  ShieldCheck, Users, X, XCircle,
} from 'lucide-react';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';

import { fmt, useCorpusFacts } from './useCorpusFacts';
import MarketingShell from './MarketingShell';
import theme from './theme.module.css';
import styles from './Home2Page.module.css';
import { fetchSiteLinks } from '../../api/siteLinksApi';

/**
 * A third public home page, at "/home2" (SRS #56).
 *
 * This one is not ours. KN designed it themselves and sent it as a rendered
 * landing page; this is that design built, section for section, in the order
 * and with the copy they wrote. Where "/home" and "/home1" were our proposals
 * to choose between, this is a specification to match — so the judgement calls
 * here are about fidelity, not about taste, and anything that looked like an
 * improvement was left alone.
 *
 * Three things could not simply be copied, and each is handled the same way
 * the rest of the site handles it:
 *
 *   figures   The design prints 2,265 policies / 150 jurisdictions / 50
 *             countries. Those are read live from the same hook "/home" and
 *             "/home1" use instead of being typed in, so the three pages can
 *             never disagree with each other or with the corpus. The layout is
 *             unchanged; only the source of the number is.
 *   palette   KN's brand colours (navy, orange, the mid blue) are not in the
 *             design-system tokens, because nothing else on the site uses
 *             them. They are declared once at the top of the stylesheet and
 *             referenced as variables everywhere else, so the page has one
 *             palette rather than literals scattered through it.
 *   the form  The demo form has no endpoint to post to — the platform has no
 *             public lead capture — so it carries what the visitor typed to
 *             the sign-up page rather than silently discarding it.
 */

/**
 * Where "Book a 20-minute demo" goes: the Knowledge Networks booking page, where
 * visitors pick a slot. Declared once because both buttons use it, so changing
 * the booking link is a one-line change. The buttons are not rendered while it
 * is empty.
 */
export const DEMO_REQUEST_HREF =
  'https://bookings.cloud.microsoft/bookwithme/user/7fb08da534b9453fb4e65025c5877847@knowledgenetworks.org?anonymous&ismsaljsauthenabled&ep=pcard';

const CHIPS = [
  { icon: Crosshair, label: 'Track' },
  { icon: FileText, label: 'Summarize' },
  { icon: Scale, label: 'Compare' },
  { icon: ClipboardList, label: 'Brief' },
] as const;

const PROBLEMS = [
  ['Updates arrive late.',
    'Newsletters and free trackers update on their own schedule, not when the law changes.'],
  ['Documents take hours to read.',
    'A single bill or regulation can run to hundreds of pages.'],
  ['Comparisons start from scratch.',
    'Every question about how two countries differ means building a new spreadsheet.'],
  ['AI chatbots don’t show their sources.',
    'Legal teams have to check every answer again, so nobody saves time.'],
] as const;

const STEPS = [
  { n: '01', icon: Crosshair, title: 'Track',
    body: 'Choose the countries, states and topics you follow. PolicyOra monitors them in real '
      + 'time and alerts you when something changes.' },
  { n: '02', icon: FileText, title: 'Summarize',
    body: 'Long bills, hearings and regulations become short, plain-language summaries. Ask '
      + 'follow-up questions & get answers linked to the original law.' },
  { n: '03', icon: Scale, title: 'Compare',
    body: 'See how different jurisdictions regulate the same issue, side by side, in seconds.' },
  { n: '04', icon: ClipboardList, title: 'Brief',
    body: 'Share clear updates with your team or leadership. Every point links back to the '
      + 'source document.' },
] as const;

const FEATURES = [
  { icon: Clock, title: 'Real-time AI policy monitoring',
    body: 'PolicyOra watches policy sources continuously, so you no longer need to check dozens '
      + 'of websites every week.' },
  { icon: MessageSquare, title: 'AI Policy Chat with linked sources',
    body: 'Ask questions like “Which AI transparency rules apply to chatbots in the EU and UK?” '
      + 'and get a clear answer with a link to the document behind it.' },
  { icon: Scale, title: 'Jurisdiction comparison',
    body: 'See how different countries and states regulate AI on one screen. Spot gaps and '
      + 'trends in seconds instead of days.' },
  { icon: Bell, title: 'Smart alerts',
    body: 'Get notified when something changes in the places and topics you follow, instead of '
      + 'finding out weeks later.' },
  { icon: Search, title: 'AI summaries and smart search',
    body: 'Search in plain language, filter by jurisdiction, topic or date, and read short '
      + 'summaries of long bills and hearings.' },
  { icon: Globe, title: 'Interactive timeline and global map',
    body: 'Follow each law from proposal to enforcement, see upcoming deadlines early, and view '
      + 'AI policy activity across 50 countries at a glance.' },
] as const;

const AUDIENCES = [
  { icon: ShieldCheck, title: 'AI governance and compliance teams',
    body: 'See which AI rules apply to their products in each market, and when.' },
  { icon: Gavel, title: 'Legal teams and law firms',
    body: 'Draft client alerts and advice faster, starting from summaries linked to the primary text.' },
  { icon: Users, title: 'Policy and public affairs consultancies',
    body: 'Monitor AI policy for many clients from one dashboard.' },
  { icon: Landmark, title: 'Government affairs teams',
    body: 'Understand how other countries and states handle the same issues.' },
  { icon: Rocket, title: 'AI founders and product teams',
    body: 'Check the rules in a new market before they launch.' },
] as const;

/** [need, manual research, free trackers, PolicyOra] — a tick where the
 *  column actually delivers the need, which is what the design marks. */
const COMPARISON = [
  ['Coverage across 150 jurisdictions', ['y', 'Only what you check'], ['y', 'Periodic updates'], 'Continuous monitoring'],
  ['Alerts on your topics', ['n', 'Basic keyword alerts'], ['n', 'None'], 'Smart alerts'],
  ['Answers with links to the law', ['n', 'None'], ['n', 'None'], 'AI Policy Chat'],
  ['Side-by-side comparison', ['n', 'Built by hand'], ['n', 'Page by page'], 'In seconds'],
  ['Summaries of bills and hearings', ['n', 'Hours of reading'], ['n', 'Static summaries'], 'Instant'],
] as const;

const FAQS = [
  ['What does PolicyOra track?',
    'AI bills, acts, regulations, executive orders, consultations and government guidance, at '
    + 'federal, state and supranational level. Every record keeps a link to the instrument it '
    + 'came from, so you can open the original text at any point.'],
  ['Can I trust the AI answers?',
    'Every answer is written only from the documents retrieved for your question, and each point '
    + 'carries a link to its source. Anything the model cannot support from those documents is '
    + 'refused rather than guessed at, which is why answers are checkable rather than merely '
    + 'confident.'],
  ['Who is PolicyOra for?',
    'Governance and compliance teams, legal teams and law firms, policy and public affairs '
    + 'consultancies, government affairs teams, and AI founders bringing a product to a new market.'],
  ['How is PolicyOra different from a newsletter?',
    'A newsletter publishes on its own schedule and cannot answer your question. PolicyOra '
    + 'monitors continuously, alerts you on the jurisdictions and topics you chose, answers '
    + 'questions in plain English with links to the law, and compares jurisdictions side by side.'],
  ['Do you offer special pricing?',
    'Yes. There are dedicated plans for academic institutions, startups, law firms and government '
    + 'bodies. The pricing page lists what each one includes.'],
] as const;

function useReveal(deps: unknown[]) {
  React.useEffect(() => {
    const nodes = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal]'));
    if (!nodes.length) return;
    // `?.` guards matchMedia being absent but not its *result*, so calling
    // `.matches` on the undefined it returns throws. Any environment without
    // matchMedia is one without animation worth gating, so treat it as reduced.
    const reduced = typeof window === 'undefined'
      || (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches ?? true);
    if (reduced || typeof IntersectionObserver === 'undefined') {
      nodes.forEach((n) => { n.dataset.shown = 'true'; });
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        (e.target as HTMLElement).dataset.shown = 'true';
        io.unobserve(e.target);
      });
    }, { rootMargin: '0px 0px -12% 0px' });
    nodes.forEach((n) => { if (n.dataset.shown !== 'true') io.observe(n); });
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

const Eyebrow: React.FC<{ children: React.ReactNode; on?: 'dark' }> = ({ children, on }) => (
  <p className={`${theme.eyebrow} ${on === 'dark' ? theme.eyebrowOnDark : ''}`}>
    <span className={theme.eyebrowDash} aria-hidden="true" />
    {children}
  </p>
);

export const Home2Page: React.FC = () => {
  // Only the three headline figures are shown here, so the growth series and
  // the plan strip are not requested at all.
  const { stats } = useCorpusFacts(
    'PolicyOra — Track AI Laws and Regulations in One Place',
    { growth: false, pricing: false },
  );
  const [open, setOpen] = useState<number | null>(null);
  // The booking link is set by the admin (Admin > Branding). Until it loads, or if it cannot
  // be loaded, the built-in link is used; an empty stored link hides the buttons.
  const [demoHref, setDemoHref] = useState<string>(DEMO_REQUEST_HREF);
  React.useEffect(() => {
    let live = true;
    fetchSiteLinks()
      .then((links) => { if (live) setDemoHref(links.demo_booking_url); })
      .catch(() => { /* keep the built-in link */ });
    return () => { live = false; };
  }, []);
  useReveal([stats?.regulations]);


  return (
    <MarketingShell>
        {/* ── hero ─────────────────────────────────────────────────────── */}
        <section className={theme.hero}>
          <div className={`${theme.wrap} ${styles.heroGrid}`}>
            <div>
              <ul className={styles.chips}>
                {CHIPS.map(({ icon: Icon, label }) => (
                  <li key={label} className={styles.chip}>
                    <Icon size={17} strokeWidth={2} /> {label}
                  </li>
                ))}
              </ul>
              <h1 className={theme.h1}>
                Track <span className={theme.accentBlue}>AI Laws</span>
                <br />and <span className={theme.accentOrange}>Regulations</span>
                <br />in One Place
              </h1>
              <p className={styles.heroBody}>
                AI rules change every week. New bills get introduced, deadlines move and
                regulators publish fresh guidance. PolicyOra is an AI policy tracker that helps
                you track, summarize, compare and brief on these changes across 150 jurisdictions
                in 50 countries. Every answer includes a link to the original law, so you can
                check it yourself before you share it.
              </p>
              <div className={theme.ctaRow}>
                <Link to="/signup" className={theme.btnPrimary}>
                  Get PolicyOra <ArrowUpRight size={16} />
                </Link>
                {demoHref && (
                  <a className={theme.btnGhost} href={demoHref} target="_blank" rel="noopener noreferrer">
                    Book a 20-minute demo <ArrowUpRight size={16} />
                  </a>
                )}
              </div>
            </div>
            <img
              className={styles.heroArt}
              src="/home2-hero-dashboard.png"
              alt="The PolicyOra dashboard, showing the global coverage map, the policy question
                   box and a panel of tracked jurisdictions."
              width={1200}
              height={739}
            />
          </div>
        </section>

        {/* ── why it matters ───────────────────────────────────────────── */}
        <section className={theme.band}>
          <div className={theme.wrap}>
            <Eyebrow>Why it matters</Eyebrow>
            <div className={styles.whyHead}>
              <h2 className={theme.h2}>Why Tracking AI Regulation Is So Hard</h2>
              <p className={theme.lead}>
                Most teams still track AI regulation by hand. They check government websites,
                read law-firm newsletters, set up keyword alerts and keep notes in spreadsheets.
                That approach worked when AI policy moved slowly. It doesn’t work anymore.
              </p>
            </div>
            <div className={styles.whyGrid} data-reveal>
              <figure className={styles.whyMedia}>
                <img src="/home2-analyst.jpg" alt="A policy analyst at a desk beside a stack of
                     printed regulatory frameworks." width={1024} height={1024} />
                <figcaption>New regulations. Too much to read.</figcaption>
              </figure>
              <div>
                <p className={styles.problemsTitle}>Here is what usually goes wrong:</p>
                <ul className={styles.problems}>
                  {PROBLEMS.map(([title, body]) => (
                    <li key={title} className={styles.problem}>
                      <XCircle className={styles.problemIcon} size={20} aria-hidden="true" />
                      <div>
                        <p className={styles.problemTitle}>{title}</p>
                        <p className={styles.problemText}>{body}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <p className={styles.warn}>
                  <AlertTriangle size={20} aria-hidden="true" />
                  <span>
                    The biggest risk isn’t the time you lose. It’s making a decision based on a
                    date or rule that has already changed.
                  </span>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ── what is PolicyOra ────────────────────────────────────────── */}
        <section className={theme.dark}>
          <div className={`${theme.wrap} ${styles.whatGrid}`}>
            <div>
              <Eyebrow on="dark">The intelligence platform</Eyebrow>
              <h2 className={`${theme.h2} ${theme.onDark}`}>What Is PolicyOra?</h2>
              <p className={`${theme.lead} ${theme.leadOnDark}`}>
                PolicyOra is an AI policy intelligence platform. It monitors AI legislation,
                regulations, hearings and government guidance, then turns them into short
                summaries, side-by-side comparisons and alerts. You can also ask questions in
                plain English and get answers that link straight to the source document.
              </p>
              <p className={theme.leadStrong}>
                Today, PolicyOra tracks {fmt(stats?.regulations)} AI policies and regulations
                across {fmt(stats?.jurisdictions)} jurisdictions, covering federal, state and
                local sources.
              </p>
            </div>
            <ul className={styles.stats} data-reveal>
              <li className={styles.statRow}>
                <span className={styles.statNum}>{fmt(stats?.regulations)}</span>
                <span className={styles.statLabel}>AI policies &amp; Regulations</span>
              </li>
              <li className={styles.statRow}>
                <span className={styles.statNum}>{fmt(stats?.jurisdictions)}</span>
                <span className={styles.statLabel}>Jurisdictions Covered</span>
              </li>
              <li className={styles.statRow}>
                <span className={styles.statNum}>{fmt(stats?.countries)}</span>
                <span className={styles.statLabel}>Countries Monitored</span>
              </li>
            </ul>
          </div>
        </section>

        {/* ── how it works ─────────────────────────────────────────────── */}
        <section className={theme.band} id="how-it-works">
          <div className={theme.wrap}>
            <Eyebrow>From change to clarity</Eyebrow>
            <div className={styles.whyHead}>
              <h2 className={theme.h2}>How PolicyOra Works</h2>
              <p className={theme.lead}>
                PolicyOra follows the same four steps your team already uses. It just does them
                faster.
              </p>
            </div>
            <ol className={styles.steps} data-reveal>
              {STEPS.map(({ n, icon: Icon, title, body }) => (
                <li key={n} className={styles.step}>
                  <div className={styles.stepTop}>
                    <span className={styles.stepNum}>{n}</span>
                    <Icon className={styles.stepIcon} size={24} aria-hidden="true" />
                  </div>
                  <h3 className={styles.stepTitle}>{title}</h3>
                  <p className={styles.stepText}>{body}</p>
                </li>
              ))}
            </ol>
            <div className={styles.centreCta}>
              <Link to="/signup" className={theme.btnPrimary}>
                Start tracking AI policy → Get PolicyOra <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>
        </section>

        {/* ── key features ─────────────────────────────────────────────── */}
        <section className={theme.dark} id="features">
          <div className={theme.wrap}>
            <Eyebrow on="dark">Intelligence, not more information</Eyebrow>
            <div className={styles.featHead}>
              <h2 className={`${theme.h2} ${theme.onDark}`}>Key Features</h2>
              <Link to="/signup" className={theme.btnPrimary}>
                Start tracking AI policy → Get PolicyOra <ArrowUpRight size={16} />
              </Link>
            </div>
            <ul className={styles.featGrid} data-reveal>
              {FEATURES.map(({ icon: Icon, title, body }) => (
                <li key={title} className={styles.feat}>
                  <Icon className={styles.featIcon} size={26} aria-hidden="true" />
                  <h3 className={styles.featTitle}>{title}</h3>
                  <p className={styles.featText}>{body}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ── who uses PolicyOra ───────────────────────────────────────── */}
        <section className={theme.tint} id="who-its-for">
          <div className={theme.wrap}>
            <div className={styles.featHead}>
              <h2 className={theme.h2}>Who Uses PolicyOra</h2>
              <Link to="/pricing" className={theme.btnPrimary}>
                See PolicyOra on your own markets <ArrowUpRight size={16} />
              </Link>
            </div>
            <ul className={styles.whoGrid} data-reveal>
              {AUDIENCES.map(({ icon: Icon, title, body }) => (
                <li key={title} className={styles.whoItem}>
                  <Icon className={styles.whoIcon} size={26} aria-hidden="true" />
                  <span className={styles.whoDash} aria-hidden="true" />
                  <h3 className={styles.whoTitle}>{title}</h3>
                  <p className={styles.whoText}>{body}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ── comparison ───────────────────────────────────────────────── */}
        <section className={theme.band}>
          <div className={theme.wrap}>
            <Eyebrow>A clearer way to track</Eyebrow>
            <div className={styles.whyHead}>
              <h2 className={theme.h2}>PolicyOra vs. Free AI Law Trackers</h2>
              <div>
                <p className={theme.lead}>
                  Free AI law trackers are useful reference pages, but they update periodically
                  and can’t answer your specific questions.
                </p>
                <p className={theme.leadLink}>Here is how PolicyOra compares:</p>
              </div>
            </div>
            <div className={styles.tableWrap} data-reveal>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th scope="col">What you need</th>
                    <th scope="col">Manual research</th>
                    <th scope="col">Free trackers</th>
                    <th scope="col" className={styles.best}>PolicyOra</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON.map(([need, manual, free, ours]) => (
                    <tr key={need}>
                      <th scope="row">{need}</th>
                      <td>
                        {manual[0] === 'y'
                          ? <Check className={styles.yes} size={17} aria-label="Yes" />
                          : <X className={styles.no} size={17} aria-label="No" />}
                        <span>{manual[1]}</span>
                      </td>
                      <td>
                        {free[0] === 'y'
                          ? <Check className={styles.yes} size={17} aria-label="Yes" />
                          : <X className={styles.no} size={17} aria-label="No" />}
                        <span>{free[1]}</span>
                      </td>
                      <td className={styles.best}>
                        <Check className={styles.yes} size={17} aria-label="Yes" />
                        <span>{ours}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* ── in practice ──────────────────────────────────────────────── */}
        <section className={theme.band}>
          <div className={`${theme.wrap} ${styles.practiceGrid}`} data-reveal>
            <div>
              <Eyebrow>In practice</Eyebrow>
              <h2 className={theme.h2}>Example: Keeping Up With the EU AI Act</h2>
              <p className={theme.lead}>
                On 27 July 2026, the EU’s Digital Omnibus on AI (Regulation (EU) 2026/1143) came
                into force and changed key EU AI Act dates. Rules for high-risk AI systems listed
                in Annex III, which include AI used in hiring and education, now apply from
                2 December 2027 instead of 2 August 2026. Rules for high-risk AI built into
                regulated products now apply from 2 August 2028.
              </p>
              <p className={theme.leadStrongDark}>
                With PolicyOra, you can ask “When do the EU AI Act high-risk rules apply now?”
                and get this answer in seconds, with a link to the Official Journal of the
                European Union.
              </p>
            </div>
            <figure className={styles.practiceMedia}>
              <img src="/home2-eu-flags.jpg" alt="European Union flags outside the Berlaymont
                   building in Brussels." width={1200} height={670} />
              <figcaption className={styles.sourceCard}>
                <span className={styles.sourceHead}>
                  <FileText size={18} aria-hidden="true" /> EU AI Act
                </span>
                <span className={styles.sourceBody}>Updated dates for high-risk AI rules</span>
                <Link to="/signup" className={styles.sourceLink}>View source →</Link>
              </figcaption>
            </figure>
          </div>
        </section>

        {/* ── built by Knowledge Networks ──────────────────────────────── */}
        <section className={`${theme.tint} ${styles.knBand}`}>
          <div className={`${theme.wrap} ${styles.knGrid}`}>
            <div>
              <h2 className={theme.h2}>Built by Knowledge Networks</h2>
              <p className={theme.lead}>
                PolicyOra is an initiative of Knowledge Networks, the Washington, D.C.
                organization behind Regulating AI. Knowledge Networks brings together
                policymakers, regulators and AI leaders from around the world, and that
                experience informs what PolicyOra tracks and how quickly it reports changes.
              </p>
            </div>
            <img className={styles.knLogo} src="/kn-wordmark.png" alt="Knowledge Networks"
                 width={600} height={207} />
          </div>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────────────── */}
        <section className={theme.band} id="faq">
          <div className={theme.wrap}>
            <Eyebrow>Your questions</Eyebrow>
            <div className={styles.faqGrid}>
              <h2 className={theme.h2}>Frequently Asked Questions</h2>
              <ul className={styles.faqList}>
                {FAQS.map(([q, a], i) => (
                  <li key={q} className={styles.faqItem}>
                    <button
                      type="button"
                      id={`h2-faq-q-${i}`}
                      className={styles.faqQ}
                      aria-expanded={open === i}
                      aria-controls={`h2-faq-a-${i}`}
                      onClick={() => setOpen(open === i ? null : i)}
                    >
                      <span>{q}</span>
                      {open === i
                        ? <Minus size={20} aria-hidden="true" />
                        : <Plus size={20} aria-hidden="true" />}
                    </button>
                    {open === i && (
                      <p
                        id={`h2-faq-a-${i}`}
                        role="region"
                        aria-labelledby={`h2-faq-q-${i}`}
                        className={styles.faqA}
                      >
                        {a}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ── closing CTA ──────────────────────────────────────────────── */}
        <section className={styles.closing}>
          <div className={`${theme.wrap} ${styles.closingInner}`}>
            <h2 className={`${theme.h2} ${theme.onDark}`}>Start Tracking AI Policy Today</h2>
            <p className={styles.closingBody}>
              AI regulation won&apos;t slow down, but your team can stay ahead of it. Track what
              matters to you, summarize it in minutes, compare markets side by side and brief
              your team with confidence.
            </p>
            <div className={theme.ctaRow}>
              <Link to="/signup" className={theme.btnPrimary}>
                Get PolicyOra <ArrowUpRight size={16} />
              </Link>
              {demoHref && (
                <a className={theme.btnGhost} href={demoHref} target="_blank" rel="noopener noreferrer">
                  Book a 20-minute demo <ArrowUpRight size={16} />
                </a>
              )}
            </div>
          </div>
        </section>
    </MarketingShell>
  );
};

export default Home2Page;
